import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { RootState, AppDispatch } from '../../../store';
import { closeAuthModal, addNotification } from '../../../store/slices/uiSlice';
import { loginUser } from '../../../store/thunks';
import './AuthModal.css';

const AuthModal: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthModalOpen, authModalMessage } = useSelector((state: RootState) => state.ui);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  if (!isAuthModalOpen) return null;

  const handleClose = () => {
    dispatch(closeAuthModal());
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setErrorMsg('Please enter both email and password.');
      return;
    }
    setIsLoading(true);
    setErrorMsg('');

    try {
      const result = await dispatch(loginUser({ email, password }));
      if (loginUser.fulfilled.match(result)) {
        dispatch(addNotification({ type: 'success', title: 'Logged In', message: 'You have signed in successfully!' }));
        dispatch(closeAuthModal());
      } else {
        setErrorMsg((result.payload as string) || 'Invalid login details.');
      }
    } catch {
      setErrorMsg('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-modal-overlay" onClick={handleClose}>
      <div className="auth-modal-card" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close" onClick={handleClose} aria-label="Close">
          ✕
        </button>

        <div className="auth-modal-banner">
          <div className="auth-modal-banner__tag">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            Sign In Required
          </div>
          <h3 className="auth-modal-banner__title">Welcome to IndustryBull</h3>
          <p className="auth-modal-banner__sub">Log in to save items to cart & wishlist and access B2B pricing.</p>
        </div>

        <div className="auth-modal-body">
          {authModalMessage && (
            <div className="auth-modal-msg">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {authModalMessage}
            </div>
          )}

          {errorMsg && (
            <div className="auth-modal-msg" style={{ background: '#FEF2F2', borderColor: '#EF4444', color: '#B91C1C' }}>
              {errorMsg}
            </div>
          )}

          <form onSubmit={handleLoginSubmit}>
            <div className="auth-modal-field">
              <label htmlFor="modal-email">Email Address</label>
              <input
                id="modal-email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="auth-modal-field">
              <label htmlFor="modal-password">Password</label>
              <input
                id="modal-password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
              />
            </div>

            <button type="submit" className="auth-modal-submit" disabled={isLoading}>
              {isLoading ? 'Signing In...' : 'Sign In & Continue'}
            </button>
          </form>

          <div className="auth-modal-footer">
            Don't have an account?{' '}
            <Link to="/auth/register" onClick={handleClose}>
              Create B2B Account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
